import { useEffect, useState } from 'react'
import { useOperationsAccess } from '../../../core/useOperationsAccess'
import { OP } from '../../../core/operationPermissions'
import { getApiErrorMessage } from '../../../../../shared/utils/apiErrorMessage'
import { loadDashboardFinalCii, DashboardCiiResult } from '../finalCii'
import type { OperationsDashboardData } from '../types'

export function useDashboardFinalCii(data: OperationsDashboardData, baseLoading: boolean, errors: Record<string, string>, year: number) {
  const { opCan, permissionsLoaded } = useOperationsAccess()
  const [state, setState] = useState<{ loading: boolean; result: DashboardCiiResult | null; error: string | null }>({ loading: false, result: null, error: null })
  useEffect(() => {
    let active = true
    const empty = { loading: false, result: null, error: null }
    if (baseLoading || !permissionsLoaded) { setState({ ...empty, loading: true }); return () => { active = false } }
    if (!opCan(OP.READ_CII_SUMMARY)) { setState({ ...empty, error: 'You do not have permission to view CII.' }); return }
    const failedSource = errors.vessels || errors.reports || errors.voyages || errors.dpr || errors.cargo
    if (failedSource) { setState({ ...empty, error: `CII source data could not be loaded: ${failedSource}` }); return }
    if (data.vessels.length !== 1) { setState(empty); return }
    setState({ ...empty, loading: true })
    void loadDashboardFinalCii({ vessel: data.vessels[0], year, reports: data.createdReports, dpr: data.dprList, voyages: data.voyages, cargo: data.cargoOps })
      .then(result => { if (active) setState({ ...empty, result }) })
      .catch(error => { if (active) setState({ ...empty, error: getApiErrorMessage(error, 'CII could not be calculated. Retry the dashboard refresh.') }) })
    return () => { active = false }
  }, [data, baseLoading, errors, year, opCan, permissionsLoaded])
  const result = state.result?.vesselId === Number(data.vessels[0]?.id) && state.result?.year === year ? state.result : null
  return { ...state, result, needsVessel: data.vessels.length !== 1 }
}
